import React from "react";
import PropTypes from 'prop-types';
import { formatNumber, getSortIndicator } from "../../../../../utils/helpers"; // Ensure helper path is correct
import useSortedData from "../../../hooks/useSortedData";
import "./PlayerPicker.css";

// Configuration for sport-specific display elements in the picker
const sportPickerConfigs = {
  Poker: {
    header: 'CHIPS',
    field: 'chipCount',
    format: (value) => (typeof value === 'number' ? formatNumber(value) : 'N/A'),
    sortKey: 'sportSpecificData.chipCount',
  },
  Golf: {
    header: 'ODDS',
    field: 'odds',
    format: (value) => (typeof value === 'number' ? `+${value}` : 'N/A'),
    sortKey: 'sportSpecificData.odds',
  },
  Chess: {
    header: 'RATING',
    field: 'rating',
    format: (value) => (typeof value === 'number' ? value : 'N/A'),
    sortKey: 'sportSpecificData.rating',
  },
  Esports: {
    header: 'SEED',
    field: 'seed',
    format: (value) => (typeof value === 'number' ? value : 'N/A'),
    sortKey: 'sportSpecificData.seed',
  },
  // Add other sports or a default if needed
  Default: {
    header: 'DATA',
    field: null,
    format: () => '-',
    sortKey: 'name', // Default sort key if sport unknown
  }
};

const PlayerPickerSalary = ({
  players,
  onPlayerSelect,
  selectedPlayers,
  disabled,
  sport // Add sport prop
}) => {
  // Get the configuration for the current sport
  const config = sportPickerConfigs[sport] || sportPickerConfigs.Default;

  // Filter out selected players first (using name comparison)
  const availablePlayers = players.filter(
    (player) => !selectedPlayers.some(selected => selected?.name === player.name) // Safer check
  );

  // Use our custom hook with initial sort by salary descending
  const { sortedData: sortedPlayers, requestSort, sortConfig } = useSortedData(
    availablePlayers,
    "salary" // Initial sort key remains salary
  );

  const handlePlayerSelect = (player) => {
    if (disabled) return;
    onPlayerSelect(player);
  };

  // Use imported getSortIndicator helper

  return (
    <div className="player-picker-container">
      <div className="player-picker-table-header">
        <span className="player-picker-table-header-text">
          PLAYERS AVAILABLE ({availablePlayers.length}) - Click to Add Player
        </span>
      </div>
      <table className="player-picker-table">
        <thead>
          <tr>
            <th onClick={() => requestSort("name")}>
              NAME {getSortIndicator(sortConfig, "name")}
            </th>
            {/* Use dynamic header and sort key */}
            <th onClick={() => requestSort(config.sortKey)}>
              {config.header} {getSortIndicator(sortConfig, config.sortKey)}
            </th>
            <th onClick={() => requestSort("salary")}>
              SALARY {getSortIndicator(sortConfig, "salary")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedPlayers.map((player) => (
            <tr
              key={player.name} // Assuming name is unique for key
              onClick={() => handlePlayerSelect(player)}
              className={`player-row ${disabled ? "disabled" : ""}`}
              aria-label={`${player.name} - Click to add to lineup`}
              role="button"
              tabIndex="0"
            >
              <td>{player.name}</td>
              {/* Use dynamic field access and formatting */}
              <td>{config.field ? config.format(player.sportSpecificData?.[config.field]) : '-'}</td>
              <td>${formatNumber(player.salary)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

PlayerPickerSalary.propTypes = {
  players: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    salary: PropTypes.number.isRequired,
    // Make sportSpecificData generic
    sportSpecificData: PropTypes.object 
  })).isRequired,
  onPlayerSelect: PropTypes.func.isRequired,
  selectedPlayers: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    // No need for other fields here if only name is used for filtering
  })).isRequired,
  disabled: PropTypes.bool,
  sport: PropTypes.string.isRequired // Add sport prop type
};

PlayerPickerSalary.defaultProps = {
  disabled: false
};

export default PlayerPickerSalary; 